import { Box, Grid, GridProps, styled } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import {
  FormCheckbox,
  LwButton,
  LwFormInput,
  LwFormMultiSelect,
  LwFormSlider,
  LWModal,
  SliderMark,
} from 'redesign';
import { Certificate, CompanyEmploymentTypes, CompanyOrt, EmploymentType } from '@types';
import { useCallback, useMemo, useState } from 'react';
import { HQCertificateWithFileUrl } from 'hq/services/accreditations-service.types';
import { useFeatureFlagContext } from 'shared/contexts/feature-flag-provider';
import { IconEdit } from 'assets/img';
import { CompanyOrtForm } from './company-ort-form';

export type CompanyFormData = {
  name: string;
  ortId: string;
  ortStartDate: string;
  workCooldownMinutes: number;
  receiptCertificates: Certificate[];
  maxFLTravelAllowance: number | undefined;
  maxParking: number | undefined;
  maxTWTravelAllowance: number | undefined;
  maxMileageAllowance: number | undefined;
} & CompanyEmploymentTypes;

export type CompanyFormState = 'create' | 'update';

type CompanyFormProps = {
  state: CompanyFormState;
  certificates: HQCertificateWithFileUrl[];
  orts: CompanyOrt[] | undefined;
};

const validateEmploymentTypes = (
  offersFreelanceWork: boolean,
  offersTempWork: boolean,
  offersEmployeeWork: boolean,
  isSubmitted: boolean
) => {
  if (isSubmitted && !offersFreelanceWork && !offersTempWork && !offersEmployeeWork) {
    return 'Er moet minimaal een arbeidsvorm geselecteerd zijn';
  }
};

const getOrtByEmploymentType = (
  orts: CompanyOrt[] | undefined,
  employmentType: EmploymentType
): CompanyOrt | undefined => {
  if (!orts || !Array.isArray(orts)) {
    return undefined;
  }
  return orts.find((ort) => ort.employmentType === employmentType);
};

export const CompanyForm = ({ state, certificates, orts }: CompanyFormProps) => {
  const {
    control,
    watch,
    formState: { isSubmitted },
  } = useFormContext<CompanyFormData>();

  const sliderMarks: SliderMark[] = useMemo(() => {
    return Array.from({ length: 25 }, (_, i) => ({ value: 60 * i, label: i.toString() }));
  }, []);

  const offersTempWork = watch('offersTempWork');
  const offersFreelanceWork = watch('offersFreelanceWork');
  const offersEmployeeWork = watch('offersEmployeeWork');

  const employmentTypeError = validateEmploymentTypes(
    offersFreelanceWork,
    offersTempWork,
    offersEmployeeWork,
    isSubmitted
  );
  const [showModal, setModal] = useState(false);
  const [ortEmploymentType, setOrtEmploymentType] = useState(EmploymentType.FREELANCER);
  const closeModal = useCallback(() => setModal(false), [showModal]);

  const certificatesOptions = useMemo(() => {
    return certificates.filter((certificate) => certificate.canHaveReceipt);
  }, [certificates]);

  const { allowExpenses } = useFeatureFlagContext();
  const disableFLExpensesFields = !allowExpenses || !offersFreelanceWork;
  const disableTWExpensesFields = !allowExpenses || !offersTempWork;

  return (
    <>
      <Box width={'100%'}>
        <Grid container item spacing={2} xs={6}>
          <Grid item xs={12}>
            <LwFormInput
              name="name"
              control={control}
              rules={{ required: 'Voer een bedrijfsnaam in' }}
              label="Bedrijfsnaam"
              disabled={state !== 'create'}
            />
          </Grid>
          {certificatesOptions.length ? (
            <Grid item xs={12}>
              <LwFormMultiSelect
                name="receiptCertificates"
                label="Certificaten die worden geaccepteerd als ontvangstbewijzen"
                data-testid="receiptCertificates"
                control={control}
                options={certificatesOptions}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionKey={(option) => option.id}
                getOptionLabel={(option) => option.name}
                helperText="Ontvangstbewijzen die een echt certificaat kunnen vervangen"
              ></LwFormMultiSelect>
            </Grid>
          ) : null}

          <Grid item xs={12} container alignItems="baseline" spacing={2}>
            <Grid item xs={6}>
              <FormCheckbox
                name="offersFreelanceWork"
                control={control}
                label="Biedt freelancewerk aan"
                rules={{
                  validate: () => employmentTypeError,
                }}
                checkboxError={employmentTypeError}
              />
            </Grid>
            <ManageOrtGrid item xs={6} container>
              <LwButton
                onClick={() => {
                  setOrtEmploymentType(EmploymentType.FREELANCER);
                  setModal(true);
                }}
                color="secondary"
                disabled={disableFLExpensesFields}
                startIcon={<IconEdit />}
              >
                Manage ORT
              </LwButton>
            </ManageOrtGrid>
            <Grid item xs={6}>
              <LwFormInput
                name="maxFLTravelAllowance"
                control={control}
                label="Max. Reiskostenvergoed"
                disabled={disableFLExpensesFields}
                inputType="number"
                placeholder="in euro's"
              />
            </Grid>
            <Grid item xs={6}>
              <LwFormInput
                name="maxParking"
                control={control}
                label="Max. Parkeerkosten"
                disabled={disableFLExpensesFields}
                inputType="number"
                placeholder="in euro's"
              />
            </Grid>
          </Grid>

          <Grid item xs={12} container alignItems="baseline" spacing={2}>
            <Grid item xs={6}>
              <FormCheckbox
                name="offersTempWork"
                control={control}
                label="Biedt uitzendwerk aan"
                rules={{
                  validate: () => employmentTypeError,
                }}
                checkboxError={employmentTypeError}
              />
            </Grid>
            <ManageOrtGrid item xs={6} container>
              <LwButton
                color="secondary"
                disabled={disableTWExpensesFields}
                startIcon={<IconEdit />}
                onClick={() => {
                  setOrtEmploymentType(EmploymentType.TEMPWORKER);
                  setModal(true);
                }}
              >
                Manage ORT
              </LwButton>
            </ManageOrtGrid>
            <Grid item xs={6}>
              <LwFormInput
                name="maxTWTravelAllowance"
                control={control}
                label="Max. Reiskostenvergoed"
                disabled={disableTWExpensesFields}
                inputType="number"
                placeholder="in euro's"
              />
            </Grid>
            <Grid item xs={6}>
              <LwFormInput
                name="maxMileageAllowance"
                control={control}
                label="Max. Kilometervergoeding"
                disabled={disableTWExpensesFields}
                inputType="number"
                placeholder="in kilometers"
              />
            </Grid>
          </Grid>

          <Grid item xs={6}>
            <FormCheckbox
              name="offersEmployeeWork"
              control={control}
              label="Biedt werk in loondienst aan"
              rules={{
                validate: () => employmentTypeError,
              }}
              checkboxError={employmentTypeError}
            />
          </Grid>
          <Grid item xs={12}>
            <LwFormSlider
              name="workCooldownMinutes"
              label="Minimale tijd tussen shifts (in uren)"
              control={control}
              min={0}
              max={1440}
              step={60}
              marks={sliderMarks}
              valueLabelFormat={(value) => `${value / 60} uur`}
              data-testid="work-cooldown-slider"
            />
          </Grid>
        </Grid>
      </Box>
      <LWModal isOpen={showModal} onClose={closeModal} testId="ort-modal">
        <CompanyOrtForm
          onClose={closeModal}
          ort={getOrtByEmploymentType(orts, ortEmploymentType)}
          title={`Manage ORT - ${ortEmploymentType}`}
        />
      </LWModal>
    </>
  );
};

const ManageOrtGrid = styled(Grid)<GridProps>(() => ({
  justifyContent: 'flex-end',
}));
